import {
  Flex,
  Card,
  Button,
  Icon,
  ColumnProps,
  Text,
  ModalMethods,
  ModalManagerContext,
  Link,
} from '@pega/cosmos-react-core';
import React, { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { appState, confirmationAlertProps, customModalProps } from '../../../../interfaces/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { StyledTable } from '../../../Utilities/custom.styled';
import { ConfirmationAlert, CustomModal } from '../../../Utilities/CustomModal';
import ActionsPopover from './ActionsPopover';
import AddPersona from './AddPersona';
import { genAiUpdatePersona } from '../AISimulatorApis';
import { ActionsPopoverFlow, MaxNumberOfCustomers } from '../../../../constants/common';
import * as XLSX from 'xlsx';
import BulkUploadPersona from '../BulkUploadPersona';

export interface PersonasProps {
  simulationStatus: string;
  connectionId: string;
  customerData: any[];
  setAccountDetails: Dispatch<SetStateAction<any>>;
  customerUploadTemplateUrl?: string;
}

const IconStyle = { height: '1rem', width: '1rem', marginRight: 5 };

const Personas: React.FC<PersonasProps> = ({
  simulationStatus,
  connectionId,
  customerData = [],
  setAccountDetails,
  customerUploadTemplateUrl,
}) => {
  const dispatch = useDispatch();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const customModalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const confirmationModalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const uploadModalMethods = useRef<ModalMethods<Partial<customModalProps>>>();

  const { create } = useContext(ModalManagerContext);
  const translate = useLocalTranslation();
  const [disableAddCustomer, setDisableAddCustomer] = useState(false);
  const disableAction = simulationStatus === 'start';
  const fileRef = useRef<any>(null);

  useEffect(() => {
    if (customerData.length >= MaxNumberOfCustomers) {
      setDisableAddCustomer(true);
    } else {
      setDisableAddCustomer(false);
    }
  }, [customerData]);
  const apiCallHandler = async (payload: any, partial: string) => {
    dispatch(updateLoaderDetails(true));
    const response = await genAiUpdatePersona(sessionId, connectionId, payload, partial);
    if (response?.status === 200) {
      setAccountDetails(response?.data);
      dispatch(updateBannerInfo(translate(`${partial}_success`), 'success'));
      dispatch(updateLoaderDetails(false));
    } else {
      dispatch(
        updateBannerInfo(
          response?.errorMessage ? translate(response?.errorMessage) : translate(`${partial}_failed`),
          'urgent',
        ),
      );
      dispatch(updateLoaderDetails(false));
    }
  };

  const onAddPersona = async (data: any) => {
    const payload = { customer: data };
    await apiCallHandler(payload, 'add_persona');
  };

  const onEditPersona = async (data: any) => {
    const payload = { customer: data };
    await apiCallHandler(payload, 'edit_persona');
  };

  const deletePersona = async (customerId: string) => {
    const payload = {
      customerId: customerId,
    };
    await apiCallHandler(payload, 'delete_persona');
  };

  const deleteConfirmation = (customerId: string, fullName: string) => {
    confirmationModalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: fullName,
        deleteChannel: () => deletePersona(customerId),
      },
      { alert: true },
    );
  };

  const handleEditScenario = (customer: any) => {
    customModalMethods.current = create(CustomModal, {
      heading: translate('edit_customer'),
      children: <AddPersona handleFormSubmit={onEditPersona} customer={customer} />,
    });
  };

  const handleAddScenario = () => {
    customModalMethods.current = create(CustomModal, {
      heading: translate('add_customer'),
      children: <AddPersona handleFormSubmit={onAddPersona} />,
    });
  };

  const data = customerData?.map((item: any, index: any) => {
    const { customerId, bio } = item;
    return {
      id: index,
      name: <Text>{bio.full_name}</Text>,
      customerId: <Text>{customerId}</Text>,
      email: <Text>{bio.email_address}</Text>,
      actions: (
        <>
          <ActionsPopover
            flow={ActionsPopoverFlow.Persona}
            scenario={item}
            handleEditScenario={handleEditScenario}
            deleteConfirmation={deleteConfirmation}
            simulationStatus={simulationStatus}
          />
        </>
      ),
    };
  });

  const columns: ColumnProps<(typeof data)[number]>[] = [
    { renderer: 'name', label: translate('name') },
    { renderer: 'customerId', label: translate('customer_id') },
    { renderer: 'email', label: translate('email') },
    { renderer: 'actions', label: '' },
  ];

  const processExcel = async (data: any) => {
    const workbook = XLSX.read(data, { type: 'binary' });
    const firstSheet = workbook.SheetNames[0];
    const excelRows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { raw: false });
    uploadModalMethods.current = create(CustomModal, {
      heading: translate('bulk_upload_customer'),
      children: (
        <BulkUploadPersona
          excelData={excelRows}
          customerData={customerData}
          setAccountDetails={setAccountDetails}
          connectionId={connectionId}
        />
      ),
    });
  };

  const onFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event?.currentTarget?.files?.[0];
    const fileType = file?.name?.split('.')?.pop() as string;
    if (fileType === 'xlsx') {
      if (typeof FileReader !== 'undefined') {
        const reader = new FileReader();
        if (reader.readAsBinaryString) {
          reader.onload = () => {
            processExcel(reader.result);
          };
          file && reader.readAsBinaryString(file);
        }
      } else {
        console.log('This browser does not support HTML5.');
      }
    }
    event.target.value = '';
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 2,
      }}
    >
      <Flex>
        <Button variant="secondary" onClick={handleAddScenario} disabled={disableAddCustomer || disableAction}>
          <Icon name="plus" style={IconStyle} />
          {translate('add_customer')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            fileRef?.current.click();
          }}
          disabled={disableAddCustomer || disableAction}
        >
          <Icon name="upload" style={IconStyle} />
          {translate('bulk_upload')}
        </Button>
        <input type="file" accept=".xlsx" ref={fileRef} onChange={onFileUpload} style={{ display: 'none' }} />

        <Text style={{ float: 'right' }}>
          <Link href={customerUploadTemplateUrl || ''} target="_blank">
            <Icon name="download" />
            {translate('download_file')}
          </Link>
        </Text>
      </Flex>
      <Card style={{ padding: 0 }} groupId="popover">
        <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={data} />
      </Card>
    </Flex>
  );
};

export default Personas;
